import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import Button from '@/components/UI/Button/Button';
import { buildExternalUrls, openZASMWindows } from '@/containers/SubCampaignDetail/Feedback/feedback-utils';
import { ERROR, errorTypes, SUCCESS } from '@/helpers/constants';
import useApiRequest from '@/hooks/useApiRequest';
import useError from '@/hooks/useError';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { setSubCampaignFeedback, setSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.action';
import { getSubCampaignFeedback, getSubCampaignInfo, getSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.reducer';
import campaignServices from '@/services/campaignServices';
import { OriginalValues, SubCampaignFeedback, SubCampaignInfoOverview } from '@/types';

type propTypes = {
  isVANCR: boolean;
  id: string;
  setFeedback: (feedback: SubCampaignFeedback) => void;
  onSetOriginValues: (changes: boolean) => void;
  subCampaignInfo: SubCampaignInfoOverview;
  index?: number;
  createZASMDisabled: boolean;
  isSelection: boolean;
  version: string;
  setURLsNotLoaded: (urls: string[]) => void;
  setOnClose?: () => void;
  subCampaignFeedback: SubCampaignFeedback;
  originalValues: OriginalValues;
  setOriginalCampaignsCount: (count: number) => void;
};

const ZasmConnectionButton = ({ isVANCR, id, setFeedback, onSetOriginValues, subCampaignInfo, index = 0, createZASMDisabled, isSelection = false,
  version, setURLsNotLoaded, setOnClose, originalValues, subCampaignFeedback, setOriginalCampaignsCount }: propTypes) => {
  const { addToast } = useToastContext();
  const [clickOnZASMtWithSave, setClickOnZASMtWithSave] = useState(false);
  const { t } = useTranslation();
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const { handleCriticalError } = useError();
  const createZASMCR = useApiRequest(campaignServices.createZASMCampaign);

  const onCreateZASMCampaign = () => {
    if (originalValues?.hasChange) {
      setFeedback({
        ...subCampaignFeedback,
        saveNeeded: true,
      });
      setClickOnZASMtWithSave(true);

      return;
    }
    createZASMCampaign();
  };

  useEffect(() => {
    if (!originalValues?.hasChange && clickOnZASMtWithSave && subCampaignFeedback?.saveNeeded) {
      delete subCampaignFeedback.saveNeeded;

      setClickOnZASMtWithSave(false);
      createZASMCampaign();
    }
  }, [subCampaignFeedback?.saveNeeded, originalValues?.hasChange, clickOnZASMtWithSave]);

  const createZASMCampaign = () => {
    setGlobalLoading(true);
    createZASMCR(id).then(() => {
      campaignServices.getSubCampaign(id, `${isVANCR ? 'van_' : ''}feedback`, version)
        .then((res) => {
          addToast(t('feedback.createZASMCrSuccess'), SUCCESS);
          setFeedback(res.module);
          onSetOriginValues(false);
          const currentCampaign = res.module.campaigns[index];
          const { campaignZASMURL, selectionZASMURL } = buildExternalUrls(
            currentCampaign.zasmIntaktSynchronization.zasmId,
            subCampaignInfo,
            currentCampaign.campaignManagerEntry.campaignNumber,
          );
          setURLsNotLoaded(openZASMWindows(campaignZASMURL, isSelection, selectionZASMURL));
          setOriginalCampaignsCount(res?.module?.campaigns?.length || 0);
        })
        .finally(() => {
          setGlobalLoading(false);

          if (setOnClose) setOnClose();
        });
    })
      .catch((error) => {
        setGlobalLoading(false);

        if (error?.code === errorTypes.ZASM_CREATE_CAMPAIGN_ERROR || error?.code === errorTypes.ZASM_SERVER_ERROR) {
          addToast(t('errors.createZASMCrError'), ERROR);

          return;
        } else if (error?.code === errorTypes.ZASM_CAMPAIGN_NOT_FOUND) {
          addToast(t('errors.zasmErrorSynch'), ERROR);
        } else if (error?.code === errorTypes.ZASM_PLANT_NOT_AVAILABLE) {
          addToast(t('errors.ZASMPlantNotAvailable'), ERROR);

          return;
        }
        handleCriticalError(error);
      });
  };

  return (
    <Button
      id={isSelection ? 'button_create_zasm_selection' : 'button_create_zasm_campaign'}
      handleClick={onCreateZASMCampaign}
      buttonStyle="PrimaryRound"
      btnText={isSelection ? t('feedback.createZASMCrSelectionButton') : t('feedback.createZASMCrButton')}
      disabled={createZASMDisabled}
    />
  );
};

const mapStateToProps = (state) => ({
  subCampaignFeedback: getSubCampaignFeedback(state),
  subCampaignInfo: getSubCampaignInfo(state),
  originalValues: getSubCampaignOriginalValues(state),
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setFeedback: setSubCampaignFeedback,
  onSetOriginValues: setSubCampaignOriginalValues,
};

export default (connect(mapStateToProps, actionCreators)(ZasmConnectionButton));
